/* =========================================================================
   DARUSSALAM UK — HOMEPAGE JAVASCRIPT
   ---------------------------------------------------------------------------
   Vanilla JS, no dependencies. Each module is self-contained and
   initializes only if its target elements exist in the DOM.
   ========================================================================= */

(function () {
  'use strict';

  const $  = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  // ---------------------------------------------------------------------
  // 1. COUNTDOWN TIMER
  //    Reads target datetime from data-end attribute. Persists across reloads
  //    because it's based on an absolute date, not session time.
  // ---------------------------------------------------------------------
  function initCountdown() {
    $$('.ds-countdown').forEach((el) => {
      const end = new Date(el.dataset.end).getTime();
      if (isNaN(end)) return;

      const hEl = $('.ds-countdown__h', el);
      const mEl = $('.ds-countdown__m', el);
      const sEl = $('.ds-countdown__s', el);

      function tick() {
        const now = Date.now();
        let diff = Math.max(0, end - now);

        const days    = Math.floor(diff / 86400000); diff -= days * 86400000;
        const hours   = Math.floor(diff / 3600000) + days * 24; diff -= (hours - days * 24) * 3600000;
        const minutes = Math.floor(diff / 60000);   diff -= minutes * 60000;
        const seconds = Math.floor(diff / 1000);

        if (hEl) hEl.textContent = String(hours).padStart(2, '0');
        if (mEl) mEl.textContent = String(minutes).padStart(2, '0');
        if (sEl) sEl.textContent = String(seconds).padStart(2, '0');

        if (end - now <= 0) {
          clearInterval(interval);
          el.closest('[data-countdown-wrap]')?.setAttribute('data-expired', 'true');
        }
      }

      tick();
      const interval = setInterval(tick, 1000);
    });
  }

  // ---------------------------------------------------------------------
  // 2. HERO SLIDER
  //    Lightweight fade slider. Auto-advance + manual controls + dots.
  //    Pauses when tab is hidden. Respects prefers-reduced-motion.
  // ---------------------------------------------------------------------
  // ---------------------------------------------------------------------
  // 2. HERO SLIDER (v3 — idempotent, correctly scoped, keyboard + touch)
  //    Fixes:
  //     - Controls/dots now found via the outer .ds-hero (they're siblings
  //       of .ds-hero__slides, not descendants)
  //     - Hover-pause hooks the outer .ds-hero so moving to arrows doesn't
  //       thrash the timer
  //     - Idempotent: marks the hero with data-ds-slider-init so re-runs
  //       from shopify:section:load don't stack listeners
  //     - Keyboard: Left/Right arrow keys when hero is focused
  // ---------------------------------------------------------------------
  function initHeroSlider() {
    const hero = $('.ds-hero');
    if (!hero) return;
    if (hero.dataset.dsSliderInit === 'true') return;   // already wired
    hero.dataset.dsSliderInit = 'true';

    const slidesWrap = $('.ds-hero__slides', hero);
    if (!slidesWrap) return;

    const slides = $$('.ds-hero__slide', slidesWrap);
    if (slides.length < 2) return;

    const dots    = $$('.ds-hero__dot', hero);
    const counter = $('.ds-hero__counter strong', hero);
    const prevBtn = $('.ds-hero__arrow--prev', hero);
    const nextBtn = $('.ds-hero__arrow--next', hero);

    const autoplay = slidesWrap.dataset.autoplay !== 'false';   // default true
    const delay    = parseInt(slidesWrap.dataset.delay, 10) || 6000;
    const reduced  = matchMedia('(prefers-reduced-motion: reduce)').matches;

    let current = 0;
    let timer   = null;

    function go(i) {
      current = (i + slides.length) % slides.length;
      slides.forEach((s, idx) => s.classList.toggle('is-active', idx === current));
      dots.forEach((d, idx)    => {
        d.classList.toggle('is-active', idx === current);
        d.setAttribute('aria-selected', idx === current ? 'true' : 'false');
      });
      if (counter) counter.textContent = String(current + 1).padStart(2, '0');
    }
    function next() { go(current + 1); }
    function prev() { go(current - 1); }

    function start() {
      stop();
      if (reduced || !autoplay) return;
      timer = setInterval(next, delay);
    }
    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    prevBtn?.addEventListener('click', () => { prev(); start(); });
    nextBtn?.addEventListener('click', () => { next(); start(); });
    dots.forEach((d, i) => d.addEventListener('click', () => { go(i); start(); }));

    /* Hover pause — bound to OUTER hero so controls are inside the pause zone */
    hero.addEventListener('mouseenter', stop);
    hero.addEventListener('mouseleave', start);

    /* Pause when tab hidden */
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stop(); else start();
    });

    /* Touch swipe */
    let touchStartX = 0;
    slidesWrap.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      stop();
    }, { passive: true });
    slidesWrap.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) { dx < 0 ? next() : prev(); }
      start();
    });

    /* Keyboard navigation when hero is in focus */
    hero.setAttribute('tabindex', '-1');
    hero.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { prev(); start(); }
      if (e.key === 'ArrowRight') { next(); start(); }
    });

    go(0);
    start();
  }

  // ---------------------------------------------------------------------
  // 3. GIFT FINDER (v2 — uses all 3 answers, no back/restart conflict,
  //                 richer recommendation matrix, smooth visual feedback)
  //    State stored in sessionStorage so navigation back doesn't reset.
  // ---------------------------------------------------------------------
  function initGiftFinder() {
    const root = $('.ds-giftfinder__card');
    if (!root) return;

    const steps       = $$('.ds-giftfinder__step', root);
    const barFill     = $('.ds-giftfinder__bar-fill', root);
    const progressTxt = $('.ds-giftfinder__progress-text', root);
    const progressPct = $('.ds-giftfinder__progress-pct', root);
    const backBtn     = $('.ds-giftfinder__back', root);
    const continueBtn = $('.ds-giftfinder__continue', root);
    const restartBtn  = $('.ds-giftfinder__restart', root);
    const storeKey    = 'ds_giftfinder_state';

    let state = { step: 0, answers: {} };
    try {
      const saved = sessionStorage.getItem(storeKey);
      if (saved) state = JSON.parse(saved);
    } catch (_) { /* ignore */ }

    function save() {
      try { sessionStorage.setItem(storeKey, JSON.stringify(state)); } catch (_) {}
    }

    /* Recommendation matrix — uses ALL three answers (who, interest, budget).
       Priority: child > non-muslim > interest-driven > who-driven.
       Merchants can tweak mappings here without touching HTML/CSS. */
    function getRecommendation() {
      const { who, interest, budget } = state.answers;

      let collection = 'best-sellers';
      let title = 'Our Top Picks';
      let desc  = 'Curated bestsellers loved by thousands of our customers.';
      let icon  = '🎁';

      if (who === 'child') {
        collection = 'kids';
        title = 'Perfect for a Child';
        desc  = 'Engaging Islamic books designed to inspire young minds.';
        icon  = '🧒';
      } else if (who === 'non-muslim') {
        collection = 'seerah';
        title = 'A Beautiful Introduction to Islam';
        desc  = 'Gentle, accessible titles that welcome readers of every background.';
        icon  = '🌍';
      } else if (interest === 'quran') {
        collection = 'quran';
        title = 'The Noble Quran Collection';
        desc  = 'From pocket editions to deluxe hardbacks — over 200 Quran editions.';
        icon  = '📖';
      } else if (interest === 'hadith') {
        collection = 'hadith';
        title = 'Authentic Hadith Collections';
        desc  = 'The most trusted English–Arabic Hadith editions.';
        icon  = '📜';
      } else if (interest === 'seerah') {
        collection = 'seerah';
        title = 'Seerah & Prophet Biography';
        desc  = 'The complete life and legacy of Prophet Muhammad ﷺ.';
        icon  = '🌙';
      } else if (interest === 'everyday') {
        collection = 'prayer-dua';
        title = 'For Daily Practice';
        desc  = 'Prayer guides, dua collections, and companions for daily worship.';
        icon  = '🤲';
      } else if (who === 'family') {
        title = 'For the Whole Family';
        desc  = 'Timeless works for shared reading and reflection.';
        icon  = '👨‍👩‍👧';
      } else if (who === 'friend') {
        collection = 'new-arrivals';
        title = 'A Thoughtful Gift';
        desc  = 'Meaningful editions that will be cherished.';
      }

      if (budget === 'premium') title = '★ Premium ' + title;
      if (budget === 'budget')  title = '💷 Budget-Friendly ' + title;

      return { collection, title, desc, icon };
    }

    function renderResult(stepEl) {
      const rec = getRecommendation();
      const iconEl  = $('.ds-giftfinder__result-icon',  stepEl);
      const titleEl = $('.ds-giftfinder__result-title', stepEl);
      const descEl  = $('.ds-giftfinder__result-desc',  stepEl);
      const linkEl  = $('.ds-giftfinder__result-link',  stepEl);
      if (iconEl)  iconEl.textContent  = rec.icon;
      if (titleEl) titleEl.textContent = rec.title;
      if (descEl)  descEl.textContent  = rec.desc;
      if (linkEl)  linkEl.href = '/collections/' + rec.collection;
    }

    function render() {
      steps.forEach((s, i) => s.classList.toggle('is-active', i === state.step));

      /* Progress only counts question steps, not the result step */
      const questionSteps = steps.filter(s => s.dataset.result !== 'true').length;
      const shownIdx = Math.min(state.step, questionSteps - 1);
      const pct = Math.round(((shownIdx + 1) / questionSteps) * 100);

      if (barFill)     barFill.style.width = pct + '%';
      if (progressTxt) progressTxt.textContent = `Step ${shownIdx + 1} of ${questionSteps}`;
      if (progressPct) progressPct.textContent = pct + '%';

      if (backBtn) backBtn.style.visibility = state.step === 0 ? 'hidden' : 'visible';

      const currentStep = steps[state.step];
      const isResult    = currentStep?.dataset.result === 'true';
      const stepKey     = currentStep?.dataset.step;
      const hasAnswer   = stepKey && state.answers[stepKey] != null;
      const isLastQ     = state.step === questionSteps - 1;

      if (continueBtn) {
        continueBtn.style.display = isResult ? 'none' : '';
        continueBtn.disabled = !hasAnswer;
        continueBtn.classList.toggle('is-ready', hasAnswer);
        continueBtn.textContent = isLastQ ? 'See My Gift →' : 'Continue →';
      }

      if (isResult) renderResult(currentStep);

      $$('.ds-giftfinder__option', root).forEach((btn) => {
        const sKey = btn.closest('.ds-giftfinder__step')?.dataset.step;
        btn.classList.toggle('is-selected', !!sKey && state.answers[sKey] === btn.dataset.value);
      });
    }

    /* Option selection */
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('.ds-giftfinder__option');
      if (!btn) return;
      const stepKey = btn.closest('.ds-giftfinder__step').dataset.step;
      state.answers[stepKey] = btn.dataset.value;
      save();
      render();
    });

    continueBtn?.addEventListener('click', () => {
      if (state.step < steps.length - 1) { state.step++; save(); render(); }
    });
    backBtn?.addEventListener('click', () => {
      if (state.step > 0) { state.step--; save(); render(); }
    });

    /* Restart is now a DISTINCT class — no conflict with back handler */
    restartBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      state = { step: 0, answers: {} };
      save();
      render();
    });

    render();
  }

  // ---------------------------------------------------------------------
  // 4. ISLAMIC QUOTE ROTATOR
  //    Reveals exactly one quote based on the section's display_mode:
  //      daily  → same quote all day, rotates each day of year
  //      random → new random pick on each page load
  //      first  → always the first
  // ---------------------------------------------------------------------
  function initQuoteRotator() {
    const section = $('.ds-quote');
    if (!section) return;
    if (section.dataset.dsQuoteInit === 'true') return;
    section.dataset.dsQuoteInit = 'true';

    const items = $$('.ds-quote__item', section);
    if (items.length === 0) return;
    if (items.length === 1) return;   // Nothing to rotate — it's already visible

    const mode = section.dataset.displayMode || 'daily';
    let index = 0;

    if (mode === 'random') {
      index = Math.floor(Math.random() * items.length);
    } else if (mode === 'daily') {
      /* Day-of-year based → same quote all day, rotates at midnight */
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 0);
      const diff  = now - start;
      const dayOfYear = Math.floor(diff / 86400000);
      index = dayOfYear % items.length;
    }
    /* mode === 'first' → keep index = 0 */

    items.forEach((item, i) => {
      item.classList.toggle('is-active', i === index);
    });
  }

  // ---------------------------------------------------------------------
  // 5. AJAX ADD-TO-CART (v3 — diagnostic logging, payload fallback,
  //                     confirmed-working against Shopify 2024 API)
  //
  //    Every stage is logged with [DS Cart] prefix so you can trace
  //    exactly where a failure happens. Tries modern items[] syntax first,
  //    falls back to legacy single-item syntax if 422 received.
  // ---------------------------------------------------------------------
  function initAjaxCart() {
    /* DISABLED: cart-drawer.js IIFE is the sole handler for .ds-product__atc clicks.
       It sends sections with /cart/add.js and calls renderContents correctly.
       This function previously ran a second handler that called renderContents({ items })
       — wrong format — and overwrote the correctly rendered drawer with empty content.
       Do not re-enable this handler without removing the one in cart-drawer.js first. */
    return;

    console.log('[DS Cart] Handler initialized');

    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.ds-product__atc');
      if (!btn) return;

      /* At this point we know it's our button — log EVERY click */
      console.log('[DS Cart] Button clicked:', btn);

      if (btn.disabled) {
        console.log('[DS Cart] Button disabled, ignoring');
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      const productTitle = btn.dataset.productTitle || 'Item';
      console.log('[DS Cart] Variant ID:', variantId, 'Product:', productTitle);

      if (!variantId || variantId === '' || variantId === 'null') {
        console.warn('[DS Cart] Missing variant ID on button:', btn);
        showToast('This product is currently unavailable', 'error');
        return;
      }

      const originalText = btn.textContent;
      btn.dataset.loading = 'true';
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        const data = await addToCart(parseInt(variantId, 10), 1);
        console.log('[DS Cart] ✓ Added successfully:', data);

        btn.dataset.added = 'true';
        btn.textContent = '✓ Added';
        showToast(`"${productTitle}" added to cart`);

        await updateCartUI();
        openCartDrawer();

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: data }));

        setTimeout(() => {
          btn.dataset.added = 'false';
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2200);
      } catch (err) {
        console.error('[DS Cart] ✗ Failed:', err);
        btn.textContent = 'Try again';
        showToast(err.message || 'Could not add to cart', 'error');
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 2400);
      } finally {
        btn.dataset.loading = 'false';
      }
    });
  }

  /* Shopify /cart/add.js — tries modern items[] payload first, falls back
     to legacy single-object payload if the theme/server doesn't accept it. */
  async function addToCart(variantId, quantity) {
    const url = '/cart/add.js';
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };

    /* Attempt 1: modern items[] array payload */
    const payloadV1 = { items: [{ id: variantId, quantity: quantity }] };
    console.log('[DS Cart] Sending POST', url, payloadV1);
    let res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payloadV1) });
    console.log('[DS Cart] Response status:', res.status);

    let data;
    try { data = await res.json(); } catch (_) { data = {}; }

    if (res.ok) return data;

    /* Attempt 2: legacy single-item payload (some older themes / custom cart apps) */
    if (res.status === 422 || res.status === 400) {
      console.log('[DS Cart] items[] rejected, retrying with legacy payload');
      const payloadV2 = { id: variantId, quantity: quantity };
      res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(payloadV2) });
      console.log('[DS Cart] Fallback response status:', res.status);
      try { data = await res.json(); } catch (_) { data = {}; }
      if (res.ok) return data;
    }

    /* Both attempts failed — surface a useful error */
    const msg = data.description || data.message || `Request failed (${res.status})`;
    throw new Error(msg);
  }

  /* Update cart count bubble in Toyo's header, then re-render any
     cart drawer sections via Shopify's Section Rendering API. */

     /* Update cart count, badge, AND re-render the cart drawer contents live */
  async function updateCartUI() {
  try {
    /* 1. Fresh cart count */
    const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    const cart    = await cartRes.json();

    /* 2. Update count — only selectors confirmed in your live DOM */
    const countSelectors = [
      '[data-cart-count]',      // dsc-count-pill in your drawer header
      '.ds-cart-count',         // your custom selector
      '.cart-count-bubble-sticky', // mobile sticky bar
      '#cart-icon-bubble'       // header cart link (contains count bubble)
    ];

    countSelectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        const inner = el.querySelector('span:not([class])');
        if (inner) inner.textContent = cart.item_count;
        else el.textContent = cart.item_count;
        if (cart.item_count > 0) el.removeAttribute('hidden');
        el.classList.toggle('is-empty', cart.item_count === 0);
      });
    });

    /* 3. Drawer rendering is handled by cart-drawer.js IIFE (sends sections with /cart/add.js).
          Do NOT call renderContents here — it receives wrong data format ({ items }) instead of
          ({ sections: { 'cart-drawer': '<html>' } }) and corrupts the drawer state. */

    /* 4. Update the [data-cart-count] pill inside the drawer header specifically */
    const drawerPill = document.querySelector('.dsc-count-pill[data-cart-count]');
    if (drawerPill) drawerPill.textContent = cart.item_count;

  } catch (err) {
    console.warn('[DS Cart] updateCartUI failed:', err);
  }
}
  /* Open Toyo's cart drawer — tries multiple known selectors/methods */
  function openCartDrawer() {
    /* Method 1: Shopify Dawn/Toyo custom element with .open() */
    const cartDrawer = document.querySelector('cart-drawer, cart-notification');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      console.log('[DS Cart] Opened via custom element .open()');
      return;
    }

    /* Method 2: Click the cart icon to trigger native drawer toggle */
    const cartTrigger = document.querySelector(
      'a[href="/cart"][aria-controls], .header__icon--cart, [data-drawer-trigger="cart"]'
    );
    if (cartTrigger) {
      cartTrigger.click();
      console.log('[DS Cart] Opened via cart icon click');
      return;
    }

    /* Method 3: Toggle visible classes on known drawer containers */
    const drawerSelectors = [
      '#CartDrawer', '#cart-drawer', '.cart-drawer', '.drawer--cart',
      '.js-drawer-open-cart', '.mini-cart', '.cart-notification'
    ];
    for (const sel of drawerSelectors) {
      const el = document.querySelector(sel);
      if (el) {
        el.classList.add('active', 'is-open', 'drawer--is-open', 'js-drawer-open');
        el.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('js-drawer-open');
        document.body.classList.add('drawer-open', 'cart-drawer-open');
        console.log('[DS Cart] Opened via class toggle on', sel);
        return;
      }
    }

    /* Method 4: Last resort — dispatch event Toyo might listen for */
    document.dispatchEvent(new CustomEvent('cart:open'));
    console.log('[DS Cart] Dispatched cart:open event (fallback)');
  }



  /* Toast with success/error variants. Lives at bottom-right of viewport. */
  let toastEl = null;
  let toastTimer = null;
  function showToast(message, type = 'success') {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ds-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.dataset.type = type;
    const icon = type === 'error' ? '⚠' : '✓';
    toastEl.innerHTML = `<span class="ds-toast__icon">${icon}</span><span>${message}</span>`;
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }

  function initProductCarousels() {
  if (window.innerWidth > 640) return;

  $$('.ds-products-grid').forEach(function(grid) {
    if (grid.closest('.ds-search-page, .ds-collection-page')) return;

    var cards = $$('.ds-product', grid);
    if (cards.length < 2) return;

    grid.classList.add('ds-products-grid--carousel');

    cards.forEach(function(card) {
      card.classList.add('ds-product--carousel-item');
    });
  });
}

  // ---------------------------------------------------------------------
  // TESTIMONIAL CAROUSEL DOT INDICATORS (mobile only)
  // ---------------------------------------------------------------------
  function initTestimonialDots() {
    const grid = $('.ds-testimonials__grid');
    const dots = $$('.ds-testimonials__dot');
    if (!grid || dots.length === 0) return;
    if (grid.dataset.dsDotsInit === 'true') return;
    grid.dataset.dsDotsInit = 'true';

    const cards = $$('.ds-testimonial', grid);
    if (cards.length === 0) return;

    /* Throttled scroll handler for dot updates */
    let ticking = false;
    grid.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const gridLeft = grid.getBoundingClientRect().left;
        let closest = 0;
        let minDist = Infinity;
        cards.forEach((card, i) => {
          const dist = Math.abs(card.getBoundingClientRect().left - gridLeft);
          if (dist < minDist) { minDist = dist; closest = i; }
        });
        dots.forEach((d, i) => d.classList.toggle('is-active', i === closest));
        ticking = false;
      });
    }, { passive: true });
  }

  function openReturnForm() {
  document.getElementById('ds-return-modal').classList.add('active');
}

function closeReturnForm() {
  document.getElementById('ds-return-modal').classList.remove('active');
}


  // ---------------------------------------------------------------------
  // 6. CATEGORY SUBMENU POPUPS
  //    Desktop mega menu: floating popup below the Quran item.
  //    Mobile menu: accordion panel that slides in below the category grid.
  // ---------------------------------------------------------------------
  function initCatSubmenus() {
    // ��─ Desktop mega-nav popup ──────────────────────────────────────────
    $$('[data-cat-sub-wrap]').forEach(function (wrap) {
      const toggle = $('[data-cat-sub-toggle]', wrap);
      const popup  = $('.ds-mega-nav__sub-popup', wrap);
      if (!toggle || !popup) return;
      if (wrap.dataset.catSubInit === 'true') return;
      wrap.dataset.catSubInit = 'true';

      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        const isOpen = popup.classList.contains('is-open');
        closeAllDesktopSubs();
        if (!isOpen) {
          popup.classList.add('is-open');
          popup.setAttribute('aria-hidden', 'false');
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    function closeAllDesktopSubs() {
      $$('.ds-mega-nav__sub-popup.is-open').forEach(function (p) {
        p.classList.remove('is-open');
        p.setAttribute('aria-hidden', 'true');
        if (p.previousElementSibling) {
          p.previousElementSibling.setAttribute('aria-expanded', 'false');
        }
      });
    }

    // Close desktop popup on outside click or Escape
    if (!window.__dsCatSubDocBound) {
      window.__dsCatSubDocBound = true;
      document.addEventListener('click', function (e) {
        if (!e.target.closest('[data-cat-sub-wrap]')) closeAllDesktopSubs();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllDesktopSubs();
      });
    }

    // ── Mobile accordion ────────────────────────────────────────────────
    $$('[data-mob-sub-toggle]').forEach(function (btn) {
      if (btn.dataset.mobSubInit === 'true') return;
      btn.dataset.mobSubInit = 'true';

      const accordion = document.getElementById('ds-mob-quran-sub');
      if (!accordion) return;

      btn.addEventListener('click', function () {
        const isOpen = accordion.classList.contains('is-open');
        accordion.classList.toggle('is-open', !isOpen);
        accordion.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    });

    $$('[data-mob-sub-close]').forEach(function (closeBtn) {
      if (closeBtn.dataset.mobSubCloseInit === 'true') return;
      closeBtn.dataset.mobSubCloseInit = 'true';

      closeBtn.addEventListener('click', function () {
        const accordion = document.getElementById('ds-mob-quran-sub');
        if (accordion) {
          accordion.classList.remove('is-open');
          accordion.setAttribute('aria-hidden', 'true');
        }
        const toggle = $('[data-mob-sub-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      });
    });
  }

  // ---------------------------------------------------------------------
  // BOOT
  // Idempotent — safe to call multiple times. Global listeners are attached
  // only once (guarded by __dsBooted flag); section-scoped re-inits happen
  // on every call so Shopify theme editor events update freshly-rendered sections.
  // ---------------------------------------------------------------------
  function boot() {
    // Section-scoped (safe to re-run — they only bind to elements that exist)
    initCountdown();
    initHeroSlider();
    initGiftFinder();
    initQuoteRotator();
    initProductCarousels();
    initTestimonialDots();
    initCatSubmenus();

  if (!window.__dsCarouselResizeBound) {
  window.__dsCarouselResizeBound = true;
  window.addEventListener('resize', initProductCarousels);
  window.addEventListener('load',   initProductCarousels);
}


    // Document-level (must only bind ONCE to avoid duplicate handlers)
    if (!window.__dsBooted) {
      window.__dsBooted = true;
      initAjaxCart();
      console.log('[DS Homepage] JS loaded and initialized ✓');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Shopify theme editor section events (guarded by __dsBooted flag
  // inside boot(), so global listeners don't get double-bound)
  document.addEventListener('shopify:section:load',   boot);
  document.addEventListener('shopify:section:select', boot);

  // Apple-like smooth scroll with easing
function smoothScrollTo(target, duration = 1000) {
  const start = window.scrollY;
  const end = target.getBoundingClientRect().top + window.scrollY;
  const distance = end - start;
  let startTime = null;

  // easeInOutCubic (Apple-like feel)
  function ease(t) {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function animation(currentTime) {
    if (!startTime) startTime = currentTime;
    const timeElapsed = currentTime - startTime;
    const progress = Math.min(timeElapsed / duration, 1);
    const easedProgress = ease(progress);

    window.scrollTo(0, start + distance * easedProgress);

    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  }

  requestAnimationFrame(animation);
}


// Event listener
document.addEventListener('click', (e) => {
  const link = e.target.closest('a[href$="#ds-iqra-path"]');
  if (!link) return;

  const target = document.getElementById('ds-iqra-path');

  if (target && (window.location.pathname === '/' || link.getAttribute('href').startsWith('#'))) {
    e.preventDefault();

    smoothScrollTo(target, 1200); // duration tweak here

    history.replaceState(null, '', '#ds-iqra-path');
  }
});
})();

/* DS Premium Mobile Menu — final reliable version */
(function () {
  if (window.__dsMobileMenuInit) return;
  window.__dsMobileMenuInit = true;

  let menu = null;

  function setup() {
    menu = document.getElementById('ds-mobile-menu');
    if (!menu) { setTimeout(setup, 200); return; }

    if (menu.parentElement !== document.body) document.body.appendChild(menu);

    const headerDrawer = document.querySelector('header-drawer');
    if (!headerDrawer) { setTimeout(setup, 200); return; }

    const summary = headerDrawer.querySelector('summary');
    const details = headerDrawer.querySelector('details');

    function open() {
      if (details && details.hasAttribute('open')) details.removeAttribute('open');
      document.body.classList.remove('overflow-hidden', 'overflow-hidden-mobile', 'overflow-hidden-tablet');
      requestAnimationFrame(() => {
        menu.setAttribute('aria-hidden', 'false');
        document.body.classList.add('ds-mm-locked');
      });
    }
    function close() {
      menu.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ds-mm-locked');
    }

    if (summary) {
      const newSummary = summary.cloneNode(true);
      summary.parentNode.replaceChild(newSummary, summary);
      newSummary.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        open();
      });
      newSummary.addEventListener('pointerdown', function (e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
    }

    if (details) {
      new MutationObserver(function () {
        if (details.hasAttribute('open')) {
          details.removeAttribute('open');
          open();
        }
      }).observe(details, { attributes: true, attributeFilter: ['open'] });
    }

    document.addEventListener('click', function (e) {
      const closer = e.target.closest('[data-ds-mm-close]');
      if (closer) {
        const link = e.target.closest('a[href]');
        if (!link) {
          e.preventDefault();
        }
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.getAttribute('aria-hidden') === 'false') close();
    });

    window.dsMobileMenu = { open, close };
    console.log('[DS MM] ✓ Ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();